import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { 
  ArrowLeft, User, Phone, Mail, Truck, MapPin, Calendar, 
  Edit2, Save, X, Shield, Bell, FileText, LogOut, Camera, Upload,
  CheckCircle, AlertCircle, Loader, CreditCard, BookOpen
} from 'lucide-react';
import { useDriverOnboarding } from '../hooks/useDriverOnboarding';
import logo from '@/assets/599ad72d49c8887573a4750887df95ce60e36150.png';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { driverService } from '../services/driverService';
import { userService } from '../services/userService';
import { documentService } from '../services/documentService';
import { authService } from '../services/authService';
import { trailerService } from '../services/trailerService';
import { vehicleService } from '../services/vehicleService';
import { settingsService } from '../services/settingsService';
import { clearStoredDriverMobileUser, getStoredDriverMobileUser, setStoredDriverMobileUser } from '../utils/driverMobileSession';
import { isExternalDriverUser } from '../utils/userRoles';
import { getStoredThemePreference, getUserThemePreferenceSettingKey, setStoredThemePreference, type ThemePreference } from '../utils/themePreference';
import { getLastKnownLocation } from '../utils/notifications';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';
import { alwaysLocationService } from '../services/alwaysLocationService';
import { Capacitor } from '@capacitor/core';

export function DriverMobileProfile() {
  const navigate = useNavigate();
  const { resetTour } = useDriverOnboarding();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [user, setUser] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [trailers, setTrailers] = useState<any[]>([]);
  const [themePreference, setThemePreference] = useState<ThemePreference>('dark');
  const [savingTheme, setSavingTheme] = useState(false);
  const [hasImpersonationSession, setHasImpersonationSession] = useState(false);
  const [lastSharedLocationAt, setLastSharedLocationAt] = useState<string | null>(null);
  const isCorporateMobileUser = isCorporateExternalTransportMobileUser(user);

  useEffect(() => {
    setHasImpersonationSession(!!localStorage.getItem('impersonationSession'));
  }, []);

  useEffect(() => {
    if (isCorporateMobileUser) return;
    const syncLastLocation = () => {
      const location = getLastKnownLocation();
      setLastSharedLocationAt(location?.timestamp || null);
    };

    syncLastLocation();
    const handleUpdate = () => syncLastLocation();
    window.addEventListener('driverLocationUpdate', handleUpdate as EventListener);
    return () => window.removeEventListener('driverLocationUpdate', handleUpdate as EventListener);
  }, [isCorporateMobileUser]);

  const handleRestoreOriginalUser = async () => {
    const original = authService.restoreOriginalSession();
    if (!original?.user) {
      alert('Ana hesap bilgisi bulunamadı');
      return;
    }
    clearStoredDriverMobileUser();
    window.location.href = '/';
  };

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    tcNo: '',
    tractorPlate: '',
    trailerPlate: '',
    truckType: '',
    city: '',
    bankName: '',
    iban: '',
    accountHolder: '',
    invoiceTitle: '',
    taxOffice: '',
    taxNumber: '',
  });

  // IBAN Document States
  const [ibanDocument, setIbanDocument] = useState<File | null>(null);
  const [ibanDocumentPreview, setIbanDocumentPreview] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    verified: boolean;
    extractedIban: string;
    extractedAccountHolder: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    const initialize = async () => {
      const parsedUser = getStoredDriverMobileUser<any>();
      if (!parsedUser) {
        navigate('/driver-mobile-auth');
        return;
      }

      try {
        const isExternalDriver = isExternalDriverUser(parsedUser);
        const response = await driverService.getDriverByUserId(parsedUser.id).catch(() => ({ data: parsedUser }));
        const driver = response.data || response || parsedUser;

        let taxMeta: any = null;
        try {
          const docsResponse = await documentService.getDocuments({ entityType: 'driver', entityId: String(parsedUser.id), documentType: 'tax-plate' });
          const docs = (docsResponse.data || []) as any[];
          const latestTaxDoc = docs[0];
          taxMeta = latestTaxDoc?.ocr_metadata
            ? (typeof latestTaxDoc.ocr_metadata === 'string' ? JSON.parse(latestTaxDoc.ocr_metadata) : latestTaxDoc.ocr_metadata)
            : null;
        } catch (docError) {
          console.error('Failed to load tax plate OCR metadata:', docError);
        }

        const mergedUser = {
          ...parsedUser,
          name: driver?.full_name || parsedUser.name || '',
          email: driver?.email || parsedUser.email || '',
          phone: driver?.phone || parsedUser.phone || '',
          tcNo: driver?.tc_number || parsedUser.tcNo || '',
          tractorPlate: driver?.cekici_plaka || parsedUser.tractorPlate || '',
          trailerPlate: driver?.dorse_plaka || parsedUser.trailerPlate || '',
          truckType: driver?.vehicle_type || parsedUser.truckType || '',
          city: driver?.city || parsedUser.city || '',
          bankName: driver?.bank_name || parsedUser.bankName || '',
          iban: driver?.iban || parsedUser.iban || '',
          accountHolder: driver?.account_holder || parsedUser.accountHolder || '',
          invoiceTitle: driver?.invoice_title || taxMeta?.invoiceTitle || parsedUser.invoiceTitle || '',
          taxOffice: driver?.tax_office || taxMeta?.taxOffice || parsedUser.taxOffice || '',
          taxNumber: driver?.tax_number || taxMeta?.taxNumber || parsedUser.taxNumber || '',
          driverType: isExternalDriver ? 'spot' : (driver?.driver_type || parsedUser.driverType || 'internal'),
          driverId: driver?.id || parsedUser.driverId || null,
          assignedVehicleId: driver?.assigned_vehicle_id || parsedUser.assignedVehicleId || null,
          applicationId: parsedUser.applicationId || null,
        };

        setUser(mergedUser);
        setFormData({
          name: mergedUser.name || '',
          phone: mergedUser.phone || '',
          email: mergedUser.email || '',
          tcNo: mergedUser.tcNo || '',
          tractorPlate: mergedUser.tractorPlate || '',
          trailerPlate: mergedUser.trailerPlate || '',
          truckType: mergedUser.truckType || '',
          city: mergedUser.city || '',
          bankName: mergedUser.bankName || '',
          iban: mergedUser.iban || '',
          accountHolder: mergedUser.accountHolder || '',
          invoiceTitle: mergedUser.invoiceTitle || '',
          taxOffice: mergedUser.taxOffice || '',
          taxNumber: mergedUser.taxNumber || '',
        });

        setStoredDriverMobileUser(mergedUser);
        setThemePreference(getStoredThemePreference(mergedUser.id));

        if (!isExternalDriver) {
          try {
            const trailerResponse: any = await trailerService.getTrailers();
            const items = Array.isArray(trailerResponse?.data) ? trailerResponse.data : [];
            setTrailers(items.filter((item: any) => !item.linked_vehicle_id || Number(item.linked_vehicle_id) === Number(mergedUser.assignedVehicleId || 0)));
          } catch (trailerError) {
            console.error('Failed to load trailers for mobile profile:', trailerError);
          }
        }
      } catch (error) {
        console.error('Error loading driver profile:', error);
        setUser(parsedUser);
        setFormData({
          name: parsedUser.name || '',
          phone: parsedUser.phone || '',
          email: parsedUser.email || '',
          tcNo: parsedUser.tcNo || '',
          tractorPlate: parsedUser.tractorPlate || '',
          trailerPlate: parsedUser.trailerPlate || '',
          truckType: parsedUser.truckType || '',
          city: parsedUser.city || '',
          bankName: parsedUser.bankName || '',
          iban: parsedUser.iban || '',
          accountHolder: parsedUser.accountHolder || '',
          invoiceTitle: parsedUser.invoiceTitle || '',
          taxOffice: parsedUser.taxOffice || '',
          taxNumber: parsedUser.taxNumber || '',
        });
      }
    };

    initialize();
  }, [navigate]);

  const handleIbanDocumentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Dosya boyutu 5MB\'dan küçük olmalıdır');
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('Sadece JPG, PNG veya PDF dosyaları yüklenebilir');
        return;
      }
      
      setIbanDocument(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setIbanDocumentPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      setTimeout(() => verifyIbanDocument(file), 500);
    }
  };

  const handleThemePreferenceChange = async (nextTheme: ThemePreference) => {
    if (!user?.id) {
      setStoredThemePreference(nextTheme);
      setThemePreference(nextTheme);
      return;
    }
    try {
      setSavingTheme(true);
      setStoredThemePreference(nextTheme, user.id);
      setThemePreference(nextTheme);
      await settingsService.saveMySetting(getUserThemePreferenceSettingKey(user.id), nextTheme, {
        category: 'user',
        description: 'Mobil tema tercihi',
        type: 'string',
      });
    } catch (error) {
      console.error('Theme preference save failed:', error);
      alert('Tema tercihi kaydedilemedi');
    } finally {
      setSavingTheme(false);
    }
  };

  const verifyIbanDocument = async (file: File) => {
    setIsVerifying(true);
    setVerificationResult(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const mockExtractedIban = 'TR33 0006 1005 1978 6457 8413 26';
      const mockExtractedAccountHolder = formData.name.toUpperCase();

      const ibanMatch = formData.iban.replace(/\s/g, '').toLowerCase().includes(mockExtractedIban.replace(/\s/g, '').toLowerCase().substring(0, 10));
      const accountHolderMatch = formData.accountHolder.toLowerCase().includes(formData.name.split(' ')[0].toLowerCase());

      if (ibanMatch && accountHolderMatch) {
        setVerificationResult({
          verified: true,
          extractedIban: mockExtractedIban,
          extractedAccountHolder: mockExtractedAccountHolder,
          message: 'IBAN belgesi başarıyla doğrulandı! IBAN ve hesap sahibi bilgileri eşleşiyor.',
        });
      } else if (!ibanMatch && accountHolderMatch) {
        setVerificationResult({
          verified: false,
          extractedIban: mockExtractedIban,
          extractedAccountHolder: mockExtractedAccountHolder,
          message: 'IBAN numarası eşleşmiyor. Lütfen IBAN bilginizi kontrol edin.',
        });
      } else if (ibanMatch && !accountHolderMatch) {
        setVerificationResult({
          verified: false,
          extractedIban: mockExtractedIban,
          extractedAccountHolder: mockExtractedAccountHolder,
          message: 'Hesap sahibi adı eşleşmiyor. Hesap sahibi ad soyad ile kayıt bilgileriniz uyumlu olmalıdır.',
        });
      } else {
        setVerificationResult({
          verified: false,
          extractedIban: mockExtractedIban,
          extractedAccountHolder: mockExtractedAccountHolder,
          message: 'IBAN ve hesap sahibi bilgileri eşleşmiyor. Lütfen belgelerinizi kontrol edin.',
        });
      }
    } catch (error) {
      console.error('OCR verification error:', error);
      setVerificationResult({
        verified: false,
        extractedIban: '',
        extractedAccountHolder: '',
        message: 'Belge doğrulanırken bir hata oluştu. Lütfen tekrar deneyin.',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const removeIbanDocument = () => {
    setIbanDocument(null);
    setIbanDocumentPreview(null);
    setVerificationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    const updatedUser = { ...user, ...formData };
    try {
      if (user?.id) {
        const isExternalDriver = isExternalDriverUser(user);
        await userService.updateUser(String(user.id), {
          full_name: formData.name,
          email: formData.email,
        });

        if (isExternalDriver && user.driverId) {
          await driverService.updateDriver(String(user.driverId), {
            full_name: formData.name,
            tc_number: formData.tcNo,
            cekici_plaka: formData.tractorPlate,
            dorse_plaka: formData.trailerPlate,
            vehicle_type: formData.truckType,
            city: formData.city,
            bank_name: formData.bankName,
            iban: formData.iban,
            account_holder: formData.accountHolder,
            invoice_title: formData.invoiceTitle,
            tax_office: formData.taxOffice,
            tax_number: formData.taxNumber,
          });
        } else if (user.driverId) {
          await driverService.updateDriver(String(user.driverId), {
            tc_number: formData.tcNo,
            cekici_plaka: formData.tractorPlate,
            dorse_plaka: formData.trailerPlate,
            vehicle_type: formData.truckType,
            city: formData.city,
            bank_name: formData.bankName,
            iban: formData.iban,
            account_holder: formData.accountHolder,
            invoice_title: formData.invoiceTitle,
            tax_office: formData.taxOffice,
            tax_number: formData.taxNumber,
          });
          if (user.assignedVehicleId) {
            await vehicleService.updateVehicle(String(user.assignedVehicleId), {
              dorse_plaka: formData.trailerPlate || null,
            });
          }
        }
      }
      setStoredDriverMobileUser(updatedUser);
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Profil guncellenirken bir hata olustu');
      return;
    }
    setUser(updatedUser);
    setIsEditing(false);
    alert('Profil bilgileriniz güncellendi!');
  };

  const handleLogout = () => {
    if (confirm('Çıkış yapmak istediğinize emin misiniz?')) {
      authService.logout();
      clearStoredDriverMobileUser();
      navigate('/driver-mobile-auth');
    }
  };

  const handleDeleteAccount = async () => {
    if (!user || !isExternalDriverUser(user)) return;
    if (!confirm('Hesabınızı kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) return;

    try {
      const response = await authService.deleteMyAccount();
      if (response.success) {
        authService.logout();
        clearStoredDriverMobileUser();
        alert('Hesabınız silindi');
        navigate('/driver-mobile-auth', { replace: true });
      } else {
        alert(response.message || 'Hesap silinemedi');
      }
    } catch (error) {
      console.error('Delete account failed:', error);
      alert('Hesap silinirken hata oluştu');
    }
  };

  if (!user) return null;

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      {/* Header */}
      <div className="relative border-b border-slate-200 bg-white p-4 pb-20 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Profilim</h1>
          <button
            onClick={() => isEditing ? setIsEditing(false) : setIsEditing(true)}
            className="rounded-xl bg-slate-100 p-2 text-slate-800 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            {isEditing ? <X className="w-5 h-5" /> : <Edit2 className="w-5 h-5" />}
          </button>
        </div>

        {/* Profile Photo */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-14">
          <div className="relative">
            <div className="flex h-28 w-28 items-center justify-center rounded-full bg-slate-100 shadow-xl border-4 border-white dark:bg-white dark:border-blue-900">
              <User className="w-16 h-16 text-gray-400" />
            </div>
            {isEditing && (
              <button className="absolute bottom-0 right-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <Camera className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pt-20 pb-32">
        {/* User Info */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{user.name || user.fullName || user.full_name || user.username || '-'}</h2>
          <p className="text-sm text-gray-600">{user.driverType === 'spot' ? 'Spot Sürücü' : 'Özmal Sürücü'}</p>
        </div>

        {/* Edit Form / Info Cards */}
        {isEditing ? (
          <div className="bg-white rounded-xl shadow-lg p-5 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Bilgilerimi Düzenle</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ad Soyad *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ad Soyad"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon Numarası *
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                  placeholder="5XXXXXXXXX"
                />
                <p className="text-xs text-gray-500 mt-1">Telefon numarası değiştirilemez</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-posta
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ornek@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TC No
                </label>
                <input
                  type="text"
                  value={formData.tcNo}
                  onChange={(e) => setFormData({ ...formData, tcNo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="12345678901"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Çekici Plaka *
                </label>
                <input
                  type="text"
                  value={formData.tractorPlate}
                  onChange={(e) => setFormData({ ...formData, tractorPlate: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="34ABC123"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dorse Plaka *
                </label>
                {(user?.role === 'internal-driver' || user?.driverType === 'internal') ? (
                  <select
                    value={formData.trailerPlate}
                    onChange={(e) => setFormData({ ...formData, trailerPlate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Dorse seçiniz</option>
                    {trailers.map((trailer: any) => (
                      <option key={trailer.id} value={trailer.plate}>{trailer.plate}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.trailerPlate}
                    onChange={(e) => setFormData({ ...formData, trailerPlate: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="34ABC123"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Araç Tipi *
                </label>
                <select
                  value={formData.truckType}
                  onChange={(e) => setFormData({ ...formData, truckType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Tenteli">Tenteli</option>
                  <option value="Açık Kasa">Açık Kasa</option>
                  <option value="Kapalı Kasa">Kapalı Kasa</option>
                  <option value="Soğutmalı">Soğutmalı</option>
                  <option value="Lowbed">Lowbed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Şehir *
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="İstanbul"
                />
              </div>

              {/* Banka Bilgileri Başlığı */}
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-green-600" />
                  <h4 className="text-md font-bold text-gray-900">Banka Bilgileri</h4>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Banka Adı
                    </label>
                    <select
                      value={formData.bankName}
                      onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Banka Seçiniz</option>
                      <option value="Ziraat Bankası">Ziraat Bankası</option>
                      <option value="İş Bankası">İş Bankası</option>
                      <option value="Garanti BBVA">Garanti BBVA</option>
                      <option value="Yapı Kredi">Yapı Kredi</option>
                      <option value="Akbank">Akbank</option>
                      <option value="Halkbank">Halkbank</option>
                      <option value="Vakıfbank">Vakıfbank</option>
                      <option value="QNB Finansbank">QNB Finansbank</option>
                      <option value="Denizbank">Denizbank</option>
                      <option value="TEB">TEB</option>
                      <option value="ING">ING</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      IBAN
                    </label>
                    <input
                      type="text"
                      value={formData.iban}
                      onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase() })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                      placeholder="TR00 0000 0000 0000 0000 0000 00"
                      maxLength={34}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      TR ile başlayan 26 haneli IBAN numaranızı giriniz
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hesap Sahibi
                    </label>
                    <input
                      type="text"
                      value={formData.accountHolder}
                      onChange={(e) => setFormData({ ...formData, accountHolder: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Metin Özşahin"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Banka hesabında kayıtlı ad soyad (Ad Soyad alanı ile aynı olmalı)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fatura Ünvanı</label>
                    <input
                      type="text"
                      value={formData.invoiceTitle}
                      onChange={(e) => setFormData({ ...formData, invoiceTitle: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Örn: Metin Özşahin Nakliyat"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vergi Dairesi</label>
                    <input
                      type="text"
                      value={formData.taxOffice}
                      onChange={(e) => setFormData({ ...formData, taxOffice: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Örn: Merkez"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vergi No</label>
                    <input
                      type="text"
                      value={formData.taxNumber}
                      onChange={(e) => setFormData({ ...formData, taxNumber: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1234567890"
                    />
                  </div>

                  {/* IBAN Document Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IBAN Belgesi
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      IBAN ve hesap sahibi bilgilerinizin doğrulanması için banka dekontunuzu yükleyiniz
                    </p>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleIbanDocumentSelect}
                      className="hidden"
                    />

                    {!ibanDocument ? (
                      <button
                        type="button"
                        onClick={() => {
                          try {
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                              fileInputRef.current.click();
                            }
                          } catch (error) {
                            console.error('File input failed:', error);
                          }
                        }}
                        className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-green-500 hover:bg-green-50 transition-colors flex flex-col items-center gap-2"
                      >
                        <Upload className="w-8 h-8 text-gray-400" />
                        <p className="text-sm font-medium text-gray-700">IBAN Belgesi Yükle</p>
                        <p className="text-xs text-gray-500">PDF, JPG veya PNG (Max 5MB)</p>
                      </button>
                    ) : (
                      <div className="border-2 border-green-500 bg-green-50 rounded-lg p-3">
                        <div className="flex items-start gap-3">
                          <FileText className="w-8 h-8 text-green-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{ibanDocument.name}</p>
                            <p className="text-xs text-gray-500">{(ibanDocument.size / (1024 * 1024)).toFixed(2)} MB</p>
                            
                            {isVerifying && (
                              <div className="mt-2 flex items-center gap-2 text-blue-600">
                                <Loader className="w-4 h-4 animate-spin" />
                                <span className="text-xs">Belge doğrulanıyor...</span>
                              </div>
                            )}

                            {verificationResult && (
                              <div className={`mt-2 p-2 rounded-lg ${
                                verificationResult.verified 
                                  ? 'bg-green-100 border border-green-300' 
                                  : 'bg-red-100 border border-red-300'
                              }`}>
                                <div className="flex items-start gap-1">
                                  {verificationResult.verified ? (
                                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1">
                                    <p className={`text-xs font-medium ${
                                      verificationResult.verified ? 'text-green-900' : 'text-red-900'
                                    }`}>
                                      {verificationResult.verified ? '✓ Doğrulandı' : '✗ Doğrulanamadı'}
                                    </p>
                                    <p className={`text-xs mt-0.5 ${
                                      verificationResult.verified ? 'text-green-700' : 'text-red-700'
                                    }`}>
                                      {verificationResult.message}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={removeIbanDocument}
                            className="p-1 hover:bg-red-100 rounded transition-colors"
                          >
                            <X className="w-5 h-5 text-red-600" />
                          </button>
                        </div>

                        {ibanDocumentPreview && !ibanDocument.type.includes('pdf') && (
                          <div className="mt-2">
                            <img
                              src={ibanDocumentPreview}
                              alt="IBAN Belgesi Önizleme"
                              className="w-full rounded-lg border"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-900">
                        <strong>ℹ️ Önemli:</strong> IBAN belgenizde IBAN, hesap sahibi ve banka adı okunaklı şekilde görünmelidir.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleSave}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
              >
                <Save className="w-5 h-5" />
                Değişiklikleri Kaydet
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-5 mb-6">
            <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Mobil Tema</h3>
                  <p className="mt-1 text-xs text-slate-500">Bu seçim sadece sizin mobil kullanım görünümünüz için kaydedilir</p>
                </div>
                {savingTheme ? <Loader className="h-4 w-4 animate-spin text-slate-500" /> : null}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleThemePreferenceChange('light')}
                  disabled={savingTheme || themePreference === 'light'}
                  className={`rounded-2xl border-2 px-4 py-3 text-sm font-bold transition ${themePreference === 'light' ? 'border-amber-400 bg-amber-400 text-slate-900 shadow-sm' : 'border-slate-200 bg-white text-slate-700'} disabled:opacity-60`}
                >
                  Light
                </button>
                <button
                  type="button"
                  onClick={() => handleThemePreferenceChange('dark')}
                  disabled={savingTheme || themePreference === 'dark'}
                  className={`rounded-2xl border-2 px-4 py-3 text-sm font-bold transition ${themePreference === 'dark' ? 'border-slate-900 bg-slate-900 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-700'} disabled:opacity-60`}
                >
                  Dark
                </button>
              </div>
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-4">Kişisel Bilgiler</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-gray-600" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Telefon</p>
                  <p className="text-sm font-medium text-gray-900">{user.phone}</p>
                </div>
              </div>

              {user.email && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Mail className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">E-posta</p>
                    <p className="text-sm font-medium text-gray-900">{user.email}</p>
                  </div>
                </div>
              )}

              {user.tcNo && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <User className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">TC Kimlik No</p>
                    <p className="text-sm font-medium text-gray-900">{user.tcNo}</p>
                  </div>
                </div>
              )}

              {user.tractorPlate && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Truck className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Çekici Plaka</p>
                    <p className="text-sm font-medium text-gray-900">{user.tractorPlate}</p>
                  </div>
                </div>
              )}

              {user.trailerPlate && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Truck className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Dorse Plaka</p>
                    <p className="text-sm font-medium text-gray-900">{user.trailerPlate}</p>
                  </div>
                </div>
              )}

              {user.truckType && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Truck className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Araç Tipi</p>
                    <p className="text-sm font-medium text-gray-900">{user.truckType}</p>
                  </div>
                </div>
              )}

              {user.city && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-gray-600" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Şehir</p>
                    <p className="text-sm font-medium text-gray-900">{user.city}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-600" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Kayıt Tarihi</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date().toLocaleDateString('tr-TR')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-5 h-5 text-gray-600" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Son Konum Paylaşımı</p>
                  <p className="text-sm font-medium text-gray-900">{lastSharedLocationAt ? new Date(lastSharedLocationAt).toLocaleString('tr-TR') : 'Henüz paylaşılmadı'}</p>
                </div>
              </div>

              {/* Banka Bilgileri - Görünüm Modu */}
              {(user.bankName || user.iban || user.accountHolder) && (
                <>
                  <div className="pt-3 border-t">
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="w-5 h-5 text-green-600" />
                      <h4 className="text-sm font-bold text-gray-900">Banka Bilgileri</h4>
                    </div>

                    {user.bankName && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-2">
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Banka</p>
                          <p className="text-sm font-medium text-gray-900">{user.bankName}</p>
                        </div>
                      </div>
                    )}

                    {user.iban && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-2">
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">IBAN</p>
                          <p className="text-sm font-medium text-gray-900 font-mono">{user.iban}</p>
                        </div>
                      </div>
                    )}

                    {user.accountHolder && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Hesap Sahibi</p>
                          <p className="text-sm font-medium text-gray-900">{user.accountHolder}</p>
                        </div>
                      </div>
                    )}

                    {(user.invoiceTitle || user.taxOffice || user.taxNumber) && (
                      <div className="grid grid-cols-1 gap-2">
                        {user.invoiceTitle && <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"><div className="flex-1"><p className="text-xs text-gray-500">Fatura Ünvanı</p><p className="text-sm font-medium text-gray-900">{user.invoiceTitle}</p></div></div>}
                        {user.taxOffice && <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"><div className="flex-1"><p className="text-xs text-gray-500">Vergi Dairesi</p><p className="text-sm font-medium text-gray-900">{user.taxOffice}</p></div></div>}
                        {user.taxNumber && <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"><div className="flex-1"><p className="text-xs text-gray-500">Vergi No</p><p className="text-sm font-medium text-gray-900">{user.taxNumber}</p></div></div>}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Settings Menu */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          <button
            onClick={() => navigate('/driver-mobile-notifications-settings')}
            className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b"
          >
            <Bell className="w-5 h-5 text-gray-600" />
            <span className="flex-1 text-left font-medium text-gray-900">Bildirim Ayarları</span>
            <span className="text-gray-400">›</span>
          </button>

          <button
            onClick={async () => {
              const opened = await alwaysLocationService.openSettingsIfAvailable();
              if (!opened || !Capacitor.isNativePlatform()) {
                navigate('/driver-mobile-notifications-settings');
              }
            }}
            className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b"
          >
            <MapPin className="w-5 h-5 text-gray-600" />
            <span className="flex-1 text-left font-medium text-gray-900">Konum Ayarları</span>
            <span className="text-xs text-gray-400">Her zaman izin ve arka plan konum takibi</span>
            <span className="text-gray-400">›</span>
          </button>

          <button
            onClick={() => navigate('/driver-mobile-security')}
            className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b"
          >
            <Shield className="w-5 h-5 text-gray-600" />
            <span className="flex-1 text-left font-medium text-gray-900">Güvenlik</span>
            <span className="text-gray-400">›</span>
          </button>

          <button
            onClick={() => navigate('/driver-mobile-documents')}
            className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b"
          >
            <FileText className="w-5 h-5 text-gray-600" />
            <span className="flex-1 text-left font-medium text-gray-900">Belgelerim</span>
            <span className="text-gray-400">›</span>
          </button>

          {isCorporateMobileUser ? (
            <>
              <button
                onClick={() => navigate('/driver-mobile-corporate-vehicles')}
                className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b"
              >
                <Truck className="w-5 h-5 text-gray-600" />
                <span className="flex-1 text-left font-medium text-gray-900">Çekiciler</span>
                <span className="text-gray-400">›</span>
              </button>

              <button
                onClick={() => navigate('/driver-mobile-corporate-drivers')}
                className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b"
              >
                <User className="w-5 h-5 text-gray-600" />
                <span className="flex-1 text-left font-medium text-gray-900">Sürücüler</span>
                <span className="text-gray-400">›</span>
              </button>
            </>
          ) : null}

          <button
            onClick={() => { resetTour(); navigate('/driver-mobile-home'); }}
            className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b"
          >
            <BookOpen className="w-5 h-5 text-gray-600" />
            <span className="flex-1 text-left font-medium text-gray-900">Uygulama Turu</span>
            <span className="text-gray-400">›</span>
          </button>

          {user?.role === 'internal-driver' || user?.driverType === 'internal' ? (
            <>
              <button
                onClick={() => navigate('/driver-mobile-vehicle-documents?tab=tractor')}
                className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b"
              >
                <Truck className="w-5 h-5 text-gray-600" />
                <span className="flex-1 text-left font-medium text-gray-900">Çekici Belgeleri</span>
                <span className="text-gray-400">›</span>
              </button>

              <button
                onClick={() => navigate('/driver-mobile-vehicle-documents?tab=trailer')}
                className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors border-b"
              >
                <Truck className="w-5 h-5 text-gray-600" />
                <span className="flex-1 text-left font-medium text-gray-900">Dorse Belgeleri</span>
                <span className="text-gray-400">›</span>
              </button>
            </>
          ) : null}

          {hasImpersonationSession && (
            <button
              onClick={handleRestoreOriginalUser}
              className="w-full flex items-center gap-3 p-4 hover:bg-amber-50 transition-colors border-b"
            >
              <ArrowLeft className="w-5 h-5 text-amber-700" />
              <span className="flex-1 text-left font-medium text-amber-700">Ana Hesaba Geri Dön</span>
            </button>
          )}

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 p-4 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-5 h-5 text-red-600" />
            <span className="flex-1 text-left font-medium text-red-600">Çıkış Yap</span>
          </button>
        </div>

        {user?.role === 'external-driver' && (
          <button
            onClick={handleDeleteAccount}
            className="w-full mt-3 bg-red-600 text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-red-700 transition-colors"
          >
            <X className="w-5 h-5" />
            Hesabımı Sil
          </button>
        )}
      </div>

      {/* Bottom Navigation */}
      <DriverBottomNav />
    </div>
  );
}
